import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const AWS_BUCKET_NAME = import.meta.env.VITE_AWS_BUCKET_NAME
export const AWS_REGION = import.meta.env.VITE_AWS_REGION

export function truncate(text: string | undefined | null, max = 45): string {
  if (!text || typeof text !== 'string') return ''
  if (text.length <= max) return text
  const slice = text.slice(0, max - 1)
  const lastSpace = slice.lastIndexOf(' ')
  const base = lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice
  return base.replace(/[\s.,;:-]*$/,'') + '…'
}

export const formatDate = (date: string | Date) => {
  return new Date(date).toLocaleDateString("vi-VN");
};
export function formatDateTime(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;

  const dd = String(d.getDate()).padStart(2, "0");
  const MM = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const HH = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");

  return `${dd}/${MM}/${yyyy} ${HH}:${mm}`;
}
export function formatMinutesToHHMMSS(minutes?: number): string {
  if (!minutes) return "00:00:000";
  const totalSeconds = minutes * 60;
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, "0");

  return `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}
export function formatSecondsToHHMMSS(seconds?: number): string {
  if (!seconds) return "00:00:00";
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const pad = (n: number) => String(n).padStart(2, "0");

  return `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}
export function normalizeUtcIso(iso: string): string {
  iso = iso.replace(/\.(\d{1,})/, (_, frac: string) => {
    const ms = frac.slice(0, 3).padEnd(3, "0");
    return `.${ms}`;
  });
  if (!/[zZ]|[+\-]\d{2}:\d{2}$/.test(iso)) {
    iso += "Z";
  }
  return iso;
}
export function formatTimeAgo(blockTime: string): string {
  if (!blockTime) return "";
  const timestampDate = new Date(blockTime);
  const currentDate = new Date();

  const timeDiff = currentDate.getTime() - timestampDate.getTime();

  const secondsDiff = Math.floor(timeDiff / 1000);
  const minutesDiff = Math.floor(secondsDiff / 60);
  const hoursDiff = Math.floor(minutesDiff / 60);
  const daysDiff = Math.floor(hoursDiff / 24);

  let timeAgo;

  if (daysDiff > 0) {
    timeAgo = `${daysDiff} days ago`;
  } else if (hoursDiff > 0) {
    timeAgo = `${hoursDiff} hours ago`;
  } else if (minutesDiff > 0) {
    timeAgo = `${minutesDiff} minutes ago`;
  } else {
    timeAgo = `${secondsDiff} seconds ago`;
  }

  return timeAgo;
}
export const truncateText = (text: string, maxLength: number) => {
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
};

export const formatAmount = (value: string | number) => {
  if (!value) return "0";

  const [intPart, decimalPart] = (
    typeof value === "number" ? value.toString() : value
  ).split(".");
  const formattedInt = Number(intPart || "0").toLocaleString("en-US");

  return decimalPart !== undefined
    ? `${formattedInt}.${decimalPart}`
    : formattedInt;
};
export function hasDecimalPart(num: number | string) {
  const number = typeof num === "string" ? +num : num;
  return Math.floor(number) !== number;
}
export function getNumberFixed(number: number | string, fix?: number) {
  const num = typeof number === "number" ? number : +number;
  if (!num) return 0;
  if (!hasDecimalPart(number)) return num;
  return +num.toFixed(fix ? fix : 4);
}

export const getAddressFormat = (address: string | undefined, end?: number) => {
  if (!address) return "";
  const frontText = address.slice(0, end ?? 4);
  const endText = address.slice(-(end ?? 4));
  return `${frontText}...${endText}`;
};

type Section = {
  total_questions: number;
  total_marks: number;
  total_time: number;
};

export function sumSectionField<T extends keyof Section>(
  sections: Section[] | undefined,
  field: T
): number {
  if (!sections) return 0;
  return sections.reduce((acc, cur) => acc + (cur[field] ?? 0), 0);
}

export function secondsToMinutes(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? "s" : ""}`;
  }

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return secs > 0
    ? `${mins} minute${mins !== 1 ? "s" : ""} ${secs} second${
        secs !== 1 ? "s" : ""
      }`
    : `${mins} minute${mins !== 1 ? "s" : ""}`;
}

export function formatDuration(seconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0))

  if (totalSeconds < 60) {
    return `${totalSeconds} s`
  }

  if (totalSeconds < 3600) {
    const mins = Math.floor(totalSeconds / 60)
    return `${mins} m`
  }

  const hours = Math.floor(totalSeconds / 3600)
  const mins = Math.floor((totalSeconds % 3600) / 60)
  return mins > 0 ? `${hours} h ${mins} m` : `${hours} h`
}

export function formatDurationToHHMM(minutes: number): string {
  const totalMinutes = Math.max(0, Math.floor(Number.isFinite(minutes) ? minutes : 0))
  if (totalMinutes < 60) {
    return `${totalMinutes} mins`
  }

  const hours = Math.floor(totalMinutes / 60)
  const mins = totalMinutes % 60
  return `${hours} hrs ${mins} mins`
}

